import React, { useEffect, useState } from 'react';
import { Box, Chip, Button, Tooltip, CircularProgress, Skeleton } from '@mui/material';
import { Stars, Add } from '@mui/icons-material';
import PaymentsService, { CreditBalance as CreditBalanceType } from '../services/payments';

interface CreditBalanceProps {
  onPurchaseClick: () => void;
  refreshTrigger?: number; // Increment this to force refresh
}

const CreditBalance: React.FC<CreditBalanceProps> = ({ onPurchaseClick, refreshTrigger }) => {
  const [balance, setBalance] = useState<CreditBalanceType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBalance = async () => {
    try {
      setLoading(true);
      const data = await PaymentsService.getCredits();
      setBalance(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch credits:', err);
      setError('Failed to load credits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, [refreshTrigger]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Skeleton matching actual layout to prevent layout shift */}
        <Skeleton variant="rounded" width={120} height={32} />
        <Skeleton variant="rounded" width={130} height={36} />
      </Box>
    );
  }

  if (error || !balance) {
    return (
      <Tooltip title="Click to purchase credits">
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={onPurchaseClick}
          sx={{ borderRadius: 2 }}
        >
          Get Credits
        </Button>
      </Tooltip>
    );
  }

  const { credits } = balance;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Credit Count */}
      <Tooltip title={credits === 1 ? "1 credit remaining" : `${credits} credits remaining`}>
        <Chip
          icon={<Stars />}
          label={`${credits} ${credits === 1 ? 'Credit' : 'Credits'}`}
          color={credits > 0 ? "primary" : "default"}
          sx={{
            fontWeight: 600,
            px: 1,
            cursor: 'default',
          }}
        />
      </Tooltip>

      {/* Purchase Button */}
      <Tooltip title="Purchase more credits">
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          onClick={onPurchaseClick}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Buy Credits
        </Button>
      </Tooltip>
    </Box>
  );
};

export default CreditBalance;
